import { motion } from 'motion/react';
import Link from 'next/link';
import { useLanguage } from '../../contexts/LanguageContext';
import { FinalCTASection } from './FinalCTASection';
import { getWpImageSrc } from '../../lib/wpMedia';

const ASSET = (id) => `/figma-assets/${id}.png`;

const DEFAULT_SERVICES = [
  'Software design',
  'Software architecture',
  'Full stack development',
  'Scalability',
  'Integrations',
  'Customer experience',
  'Agile development',
];

const DEFAULT_SERVICES_FI = [
  'Ohjelistosuunnittelu',
  'Ohjelmistoarkkitehtuuri',
  'Ketterä ohjelmistokehitys',
  'Full stack -kehitys',
  'Skaalautuvuus',
  'Integraatiot',
  'Asiakaskokemus',
];

const DEFAULT_TECHNOLOGIES = ['TypeScript', 'Node.js', 'React', 'AWS', 'MSSQL'];

const DEFAULT_KEY_AREAS = [
  { title: 'Customer focus and usability', description: 'Development has focused strongly on clarity of customer paths and user experience.', icon: '🎯' },
  { title: 'Scalability and integrations', description: 'Services have been optimized for the needs of a growing number of users and seamless integration with existing systems.', icon: '📈' },
  { title: 'Information security', description: 'The security and confidentiality of patient information is a critical foundation that guides all development decisions.', icon: '🔒' },
  { title: 'Agility and strengthening the way teams work', description: 'Efficient ways of working within the team have been at the heart of the development work.', icon: '⚡' },
];

const DEFAULT_SUMMARY_POINTS = [
  'Modernising legacy systems, technology choices and ensuring scalability',
  'Planning new features and full stack development',
  'Developing the customer path and user experience',
  'Developing working methods and practices',
];

function parseList(value) {
  if (!value) return null;
  if (Array.isArray(value)) return value.map((item) => (typeof item === 'object' && item?.label != null ? item.label : String(item)));
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return value.split(/\n/).map((s) => s.trim()).filter(Boolean);
    }
  }
  return null;
}

/** Parse our_work HTML or plain text into an array of lines (one per "button"). */
function parseOurWorkToLines(value) {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const stripped = trimmed.replace(/<[^>]+>/g, '\n').replace(/&nbsp;/g, ' ');
  const lines = stripped.split(/\n/).map((s) => s.trim()).filter(Boolean);
  return lines.length ? lines : null;
}

function parseKeyAreas(value) {
  if (!value) return null;
  if (Array.isArray(value)) return value.map((item) => ({ title: item.title ?? '', description: item.description ?? '', icon: item.icon ?? '•' }));
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      // Textarea format: one line per item, "Title|Description|Icon" (icon optional)
      const lines = trimmed.split(/\n/).map((s) => s.trim()).filter(Boolean);
      return lines.map((line) => {
        const parts = line.split(/\|/).map((p) => p.trim());
        return {
          title: parts[0] ?? '',
          description: parts[1] ?? '',
          icon: parts[2] ?? '•',
        };
      });
    }
  }
  return null;
}

function imageUrl(media) {
  return media?.node?.sourceUrl ?? media?.sourceUrl ?? null;
}

/** Renders ACF value as plain text or HTML (for WYSIWYG). Content is from WordPress admin only (trusted source; see docs/SECURITY.md). */
function RichText({ value, tag: Tag = 'div', className = '' }) {
  if (value == null || value === '') return null;
  const isHtml = typeof value === 'string' && /<[a-z][\s\S]*>/i.test(value);
  if (isHtml) {
    return <Tag className={className} dangerouslySetInnerHTML={{ __html: value }} />;
  }
  return <Tag className={className}>{value}</Tag>;
}

export function CaseStudy({ caseStudy: acf, post: postProp, lang: langProp, slug: slugProp, fiSlug: fiSlugProp, enSlug: enSlugProp } = {}) {
  const [lang, setLang] = useLanguage();
  const effectiveLang = langProp ?? lang;
  const slug = slugProp ?? null;
  // FI/EN switcher: use translation slugs from Polylang when available. When we have no fiSlug/enSlug
  // from the API, strip trailing -N from slug as fallback for the FI link (EN→FI).
  const stripTrailingNumber = (s) => (typeof s === 'string' ? s.replace(/-\d+$/, '') : s);
  const fiSlug = fiSlugProp ?? (effectiveLang === 'en' && slug ? stripTrailingNumber(slug) : slug);
  const enSlug = enSlugProp ?? slug;

  const services = parseList(acf?.services) ?? DEFAULT_SERVICES;
  const technologies = Array.isArray(acf?.technologies)
    ? acf.technologies.filter(Boolean)
    : parseList(acf?.technologies) ?? [];
  const hasTechnologies = technologies.length > 0;
  const summaryPoints = Array.isArray(acf?.summaryPoints)
    ? acf.summaryPoints.map((i) => i?.point ?? i?.label ?? (typeof i === 'string' ? i : '')).filter(Boolean)
    : (parseList(acf?.summaryPoints) ?? DEFAULT_SUMMARY_POINTS);
  // Our work: FI/EN from ACF when present, else fallback to generic ourWork
  const ourWorkHtml =
    effectiveLang === 'fi'
      ? (acf?.ourWorkFi ?? acf?.ourWork ?? null)
      : (acf?.ourWorkEn ?? acf?.ourWork ?? null);
  const ourWorkItems = parseOurWorkToLines(ourWorkHtml);
  const ourWorkDisplayItems = ourWorkItems ?? (effectiveLang === 'fi' ? DEFAULT_SERVICES_FI : DEFAULT_SERVICES);
  const ourWorkTitle = effectiveLang === 'fi' ? 'Työnkuvamme' : 'OUR WORK';
  const technologiesTitle = effectiveLang === 'fi' ? 'Teknologiat' : 'Technologies';
  const summaryTitle = effectiveLang === 'fi' ? 'Yhteenveto' : 'Summary';
  const contactSectionTitle = effectiveLang === 'fi' ? 'Tarvitsetko täsmäosaamista oman tiimisi tueksi?' : 'Interested in cooperation?';
  const contactSectionBody = effectiveLang === 'fi'
    ? 'Vahvakin in-house-tiimi tarvitsee rinnalleen luotettavan kumppanin. Ole yhteydessä, niin kartoitetaan 15 minuutissa, millainen asiantuntija toisi juuri oikeanlaista osaamista tiimiinne.'
    : "We'll be happy to tell you more about the work we do.";
  const contactButtonText = effectiveLang === 'fi' ? 'Ota yhteyttä' : 'Talk to Our Team';
  const clientLabel = acf?.clientLabel ?? 'MEHILÄINEN';
  const headline = acf?.headline ?? "Partnering to empower the development of customer-centric digital healthcare";
  const intro = acf?.intro ?? "Mehiläinen is on a lifelong mission. To serve its customers continuously better, to offer them digital services that genuinely support their wellbeing. Identio has been a long-term and trusted development partner of Mehiläinen's digital services.";
  const heroImageUrl = imageUrl(acf?.heroImage) ?? postProp?.featuredImage ?? ASSET('22b465140da82d91754c8df61bf72fdd697fb272');
  // Fallback client logo: when ACF client_logo is empty, use /logos/{slug}.png for known clients (e.g. Visma Sirius -> visma)
  const clientLogoSlugMap = { 'mehiläinen': 'mehilainen', 'visma sirius': 'visma' };
  const clientLogoSlug = clientLabel && clientLogoSlugMap[clientLabel.toLowerCase().trim()];
  const clientLogoUrl = imageUrl(acf?.clientLogo) ?? (clientLogoSlug ? `/logos/${clientLogoSlug}.png` : null) ?? ASSET('812fd3c27477ec35e929485135089474b72edbc5');

  // First content box: render full WYSIWYG from ACF
  const firstContentRaw = acf?.bodyText1 ?? null;
  const hasFirstContent = firstContentRaw && firstContentRaw.trim().length > 0;
  const bodyText1 = hasFirstContent
    ? firstContentRaw
    : (effectiveLang === 'fi'
      ? 'Mehiläinen tunnetaan myös asiakaslähtöisistä ja turvallisista digitaalisista ratkaisuistaan. Pitkäjänteisen yhteistyömme aikana olemme olleet mukana auttamassa Mehiläistä saavuttamaan liiketoiminnallisia tavoitteitaan teknisen osaamisemme kautta. Mehiläisen digitaalisten palvelujen parissa työskentelee yli 200 asiantuntijaa, jotka kehittävät päivittäin ratkaisuja parantaakseen miljoonien suomalaisten pääsyä laadukkaaseen terveydenhuoltoon. Idention konsultit ovat työskennelleet osana näiden asiantuntijoiden muodostamia kehitystiimejä.'
      : "Identio's experts have been working on Mehiläinen's digital services since 2018. The development teams have varied from smaller task-specific teams to full development teams working on different digital services.");
  const rawTestimonialQuote = acf?.testimonialQuote;
  const hasTestimonialQuote = rawTestimonialQuote != null && String(rawTestimonialQuote).trim() !== '';
  const testimonialQuote = hasTestimonialQuote ? rawTestimonialQuote : '';
  const testimonialAuthor = acf?.testimonialAuthor ?? 'Ossi Laukkanen';
  const testimonialTitle = acf?.testimonialTitle ?? 'Business Unit Director, Digital Health Services';
  // Second WYSIWYG: Key areas section (replaces heading + intro + "Key areas of cooperation" + boxes)
  const DEFAULT_KEY_AREAS_CONTENT_FI = `<h2>Kokonaisvaltaista ohjelmistokehitystä eri osa-alueilla</h2>
<p>Olemme työskennelleet yhdessä sekä järjestelmien päivityksen ja modernisoinnin että uusien kokonaisuuksien ja ominaisuuksien kehittämisessä. Yhteistyöhömme on sisältynyt monia kehitysprojektien kannalta kriittisiä vaiheita, kuten teknologiavalintojen tekemistä ja työskentelytapojen määrittelyä ja kehitystä.</p>
<h3>Yhteistyön keskeisiä osa-alueita</h3>
<ul>
<li>Asiakaslähtöisyys ja helppokäyttöisyys: kehityksessä on keskitytty vahvasti asiakaspolkujen ja käyttökokemuksen selkeyteen.</li>
<li>Skaalautuvuus ja integraatiot: palvelut on optimoitu kasvavan käyttäjämäärän tarpeisiin ja niissä on huomioitu mahdolliset tulevaisuuden integraatiot.</li>
<li>Tietoturva: potilastietojen turvallisuus sekä luottamuksellisuus ovat Mehiläisen toiminnalle kriittinen perusta.</li>
<li>Ketteryys ja tiimien työskentelytapojen vahvistaminen: kehitystyön keskiössä ovat olleet tiimiin tehokkaat työskentelytavat.</li>
</ul>`;
  const DEFAULT_KEY_AREAS_CONTENT_EN = `<h2>Comprehensive software development in different areas</h2>
<p>We have worked together on upgrading and modernising systems, as well as planning and developing completely new features. Both small, task-specific teams and larger, cross-functional development teams have been involved in the work.</p>
<h3>Key areas of cooperation</h3>
<ul>
<li>Customer focus and usability: development has focused strongly on clarity of customer paths and user experience.</li>
<li>Scalability and integrations: services have been optimized for the needs of a growing number of users and seamless integration with existing systems.</li>
<li>Information security: the security and confidentiality of patient information is a critical foundation that guides all development decisions.</li>
<li>Agility and strengthening the way teams work: efficient ways of working within the team have been at the heart of the development work.</li>
</ul>`;
  const keyAreasContent =
    acf?.bodyText2 ??
    (effectiveLang === 'fi' ? DEFAULT_KEY_AREAS_CONTENT_FI : DEFAULT_KEY_AREAS_CONTENT_EN);
  const featureImageUrl = imageUrl(acf?.featureImage) ?? ASSET('3016274d8b39826601624c720b21a2f81ee4df31');
  const contactPhotoUrl = imageUrl(acf?.contactPhoto) ?? '/team/joonas-korgan.png';
  const contactName = acf?.contactName ?? 'Joonas Korgan';
  const contactTitle = acf?.contactTitle ?? 'Head of Sales';
  // Highlight colors from ACF: darker = logo bg, client name, section titles, checkmarks; lighter = quote & summary backgrounds
  const highlightDark = acf?.highlightColorDark ?? '#0f172a';
  const highlightLight = acf?.highlightColorLight ?? '#f0fdf4';
  // Escape for use in CSS (hex or rgb is safe; avoid injecting unescaped characters)
  const highlightDarkCss = highlightDark.replace(/["\\]/g, (c) => (c === '"' ? '\\"' : '\\\\'));

  return (
    <div className="bg-white case-study-root" style={{ ['--case-study-highlight']: highlightDark }} suppressHydrationWarning>
      <style dangerouslySetInnerHTML={{ __html: `
        .case-study-root .prose ul li::before { background: ${highlightDarkCss} !important; background-color: ${highlightDarkCss} !important; }
        .case-study-root .prose ol li::before { background: ${highlightDarkCss} !important; background-color: ${highlightDarkCss} !important; color: #fff !important; }
        .case-study-root .prose h1::before, .case-study-root .prose h2::before, .case-study-root .prose h3::before { background: ${highlightDarkCss} !important; background-color: ${highlightDarkCss} !important; }
        .case-study-root .prose img { box-shadow: none !important; padding: 0.5rem; width: auto !important; max-width: 100% !important; height: auto !important; }
      ` }} />
      <section className="w-full px-2 sm:px-4 lg:px-6 py-4 lg:py-6">
        <div className="w-full max-w-[2400px] mx-auto rounded-2xl overflow-hidden relative flex flex-col min-h-0 lg:min-h-[75vh] lg:justify-end">
          {/* Hero image: 2:1 on mobile, full overlay on lg; rounded corners on all sides */}
          <div className="relative w-full aspect-[2/1] lg:aspect-auto lg:min-h-0 lg:absolute lg:inset-0 rounded-2xl overflow-hidden">
            <img
              src={getWpImageSrc(heroImageUrl) || heroImageUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-center lg:object-right rounded-2xl"
            />
            {/* Gradient overlay: desktop only, for text readability */}
            <div
              className="hidden lg:block absolute inset-0 w-full h-full"
              style={{
                background: `linear-gradient(to right, rgba(15,23,42,0.97) 0%, rgba(15,23,42,0.95) 30%, rgba(15,23,42,0.7) 50%, rgba(15,23,42,0.35) 70%, transparent 88%)`,
              }}
            />
          </div>

          {/* Content: light bg on mobile, overlay on lg — align with page content (max-w-7xl) */}
          <div className="relative z-10 bg-white lg:bg-transparent w-full px-6 sm:px-8 lg:px-12 py-12 md:py-16 lg:py-20 lg:py-24">
            <div className="max-w-7xl mx-auto w-full">
              <div className="max-w-2xl lg:[text-shadow:0_1px_2px_rgba(0,0,0,0.4)]">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-sm tracking-wider font-semibold mb-4 uppercase"
              style={{ letterSpacing: '0.15em', color: highlightDark }}
            >
              {clientLabel}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="heading-1 text-gray-900 lg:text-white text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] xl:text-[3.75rem] leading-tight font-extrabold tracking-tight lg:[text-shadow:0_2px_8px_rgba(0,0,0,0.5)]"
            >
              {headline}
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="text-sm md:text-base lg:text-xl text-gray-600 lg:text-slate-100 leading-relaxed mt-6 max-w-xl prose prose-sm md:prose-base lg:prose-lg max-w-none prose-p:mb-3 last:prose-p:mb-0 prose-p:text-gray-600 lg:prose-p:text-slate-100 lg:[text-shadow:0_1px_3px_rgba(0,0,0,0.4)]"
            >
              <RichText value={intro} tag="div" />
            </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#f9fafb] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-sm tracking-wider font-semibold text-center mb-8"
            style={{ letterSpacing: '0.1em', color: highlightDark }}
          >
            {ourWorkTitle}
          </motion.p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {ourWorkDisplayItems.map((label, index) => (
              <motion.div
                key={`${label}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-white border border-[#e5e7eb] rounded-lg px-6 py-4 text-center text-[#4b5563] font-medium md:hover:border-[var(--highlight-dark)] md:hover:shadow-md transition-all duration-200"
                style={{ ['--highlight-dark']: highlightDark }}
              >
                {label}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 min-w-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 min-w-0">
          {hasTechnologies && (
            <motion.aside
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <div className="lg:sticky lg:top-32 bg-[#f9fafb] rounded-2xl p-6 border border-[#e5e7eb]" style={{ ['--highlight-dark']: highlightDark }}>
                <h3 className="text-xl font-bold mb-6" style={{ color: highlightDark }}>
                  {technologiesTitle}
                </h3>
                <div className="flex flex-col gap-3">
                  {technologies.map((tech) => (
                    <div
                      key={tech}
                      className="bg-white border border-[#e5e7eb] rounded-lg px-5 py-3 text-sm font-medium text-[#4b5563] md:hover:border-[var(--highlight-dark)] md:hover:shadow-md transition-all duration-200"
                    >
                      {tech}
                    </div>
                  ))}
                </div>
              </div>
            </motion.aside>
          )}

          <div className={`min-w-0 ${hasTechnologies ? 'lg:col-span-9' : 'lg:col-span-12'}`}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="prose prose-lg max-w-none text-[#1a1a1a] [&_h1]:text-3xl [&_h1]:md:text-4xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:before:bg-[var(--case-study-highlight)] [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:font-bold [&_h2]:mb-4 [&_h2]:before:bg-[var(--case-study-highlight)] [&_h3]:text-xl [&_h3]:md:text-2xl [&_h3]:font-bold [&_h3]:mb-3 [&_h3]:before:bg-[var(--case-study-highlight)] [&_h4]:text-lg [&_h4]:font-bold [&_h4]:mb-3 [&_p]:text-[#4b5563] [&_p]:text-lg [&_p]:leading-relaxed [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:[&_li]:before:!bg-[var(--case-study-highlight)] [&_ol]:[&_li]:before:!bg-[var(--case-study-highlight)] [&_ol]:[&_li]:before:!text-white [&_li]:mb-1 [&_li]:text-[#1a1a1a] [&_img]:max-w-full [&_img]:h-auto [&_img.alignright]:float-right [&_img.alignright]:ml-4 [&_img.alignright]:mb-3 [&_img.alignleft]:float-left [&_img.alignleft]:mr-4 [&_img.alignleft]:mb-3 [&_img.aligncenter]:block [&_img.aligncenter]:mx-auto after:content-[''] after:table after:clear-both"
            >
              <RichText value={bodyText1} tag="div" />
            </motion.div>
          </div>
        </div>
      </section>

      {hasTestimonialQuote && (
      <section className="py-20" style={{ backgroundColor: highlightLight }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="mb-8">
              <svg
                className="w-12 h-12 opacity-30 mx-auto"
                fill="currentColor"
                viewBox="0 0 24 24"
                style={{ color: highlightDark }}
              >
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
            </div>

            <blockquote className="text-base md:text-lg text-[#1a1a1a] leading-relaxed mb-8">
              {typeof testimonialQuote === 'string' && !/<[a-z][\s\S]*>/i.test(testimonialQuote) ? (
                <> &ldquo;{testimonialQuote}&rdquo; </>
              ) : (
                <RichText value={testimonialQuote} tag="span" className="[&>p]:inline [&>p]:mb-0" />
              )}
            </blockquote>

            <div className="border-t pt-6" style={{ borderColor: `${highlightDark}33` }}>
              <p className="font-bold text-[#1a1a1a] mb-1">{testimonialAuthor}</p>
            </div>
          </motion.div>
        </div>
      </section>
      )}

      <section className="max-w-7xl mx-auto px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="prose prose-lg max-w-none text-[#1a1a1a] mb-16 [&_h1]:text-3xl [&_h1]:md:text-4xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:before:bg-[var(--case-study-highlight)] [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:font-bold [&_h2]:mb-4 [&_h2]:before:bg-[var(--case-study-highlight)] [&_h3]:text-xl [&_h3]:md:text-2xl [&_h3]:font-bold [&_h3]:mb-3 [&_h3]:before:bg-[var(--case-study-highlight)] [&_h4]:text-lg [&_h4]:font-bold [&_h4]:mb-3 [&_p]:text-[#4b5563] [&_p]:text-lg [&_p]:leading-relaxed [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:[&_li]:before:!bg-[var(--case-study-highlight)] [&_ol]:[&_li]:before:!bg-[var(--case-study-highlight)] [&_ol]:[&_li]:before:!text-white [&_li]:mb-1 [&_li]:text-[#1a1a1a] [&_img]:max-w-full [&_img]:h-auto [&_img.alignright]:float-right [&_img.alignright]:ml-4 [&_img.alignright]:mb-3 [&_img.alignright]:mt-1 [&_img.alignleft]:float-left [&_img.alignleft]:mr-4 [&_img.alignleft]:mb-3 [&_img.alignleft]:mt-1 [&_img.aligncenter]:block [&_img.aligncenter]:mx-auto [&_img.aligncenter]:my-4 after:content-[''] after:table after:clear-both">
            <RichText value={keyAreasContent} tag="div" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl overflow-hidden"
          >
            <img
              src={getWpImageSrc(featureImageUrl) || featureImageUrl}
              alt=""
              className="w-full h-auto"
            />
          </motion.div>
        </motion.div>
      </section>

      <section className="bg-[#f9fafb] py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="heading-2 text-2xl md:text-3xl font-bold text-center mb-20 text-gray-900 relative pt-5 before:content-[''] before:absolute before:top-0 before:left-1/2 before:-translate-x-1/2 before:w-[60px] before:h-1 before:rounded-sm before:bg-[var(--case-study-highlight)]"
          >
            {summaryTitle}
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {summaryPoints.map((point, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className="group relative flex flex-col p-6 lg:p-8 rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm transition-all duration-300 md:hover:shadow-[0_16px_48px_-12px_rgba(0,0,0,0.12)] md:hover:border-gray-300"
              >
                <div className="flex items-start gap-5">
                  <div
                    className="flex items-center justify-center w-11 h-11 rounded-xl flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: highlightDark }}
                    aria-hidden
                  >
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <RichText
                      value={point}
                      tag="div"
                      className="prose prose-base max-w-none text-[#374151] leading-relaxed [&_.prose]:text-[#374151] [&_p]:mb-0 [&_p:last-child]:mb-0"
                    />
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <FinalCTASection
        variant="withForm"
        title={contactSectionTitle}
        subtitle={contactSectionBody}
        contactPhotoUrl={contactPhotoUrl}
        contactName={contactName}
        contactTitle={contactTitle}
        contactButtonText={contactButtonText}
      />
    </div>
  );
}
